import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    InputAdornment,
    LinearProgress,
    Paper,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    alpha,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    Clear as ClearIcon,
    DeleteOutline as DeleteOutlineIcon,
    PersonAdd as PersonAddIcon,
    Refresh as RefreshIcon,
    Search as SearchIcon,
    Inventory2Outlined as ArchiveIcon,
    Public as PublicIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
    bulkDeleteWorkersRequest,
    bulkUpdateWorkersRequest,
    fetchWorkersRequest,
    fetchWorkerCountsByStatus,
    fetchWorkerCountsByAssignee,
} from "../../api/workersApi";
import { usersApi } from "../../api/usersApi";
import { SettingsContext } from "../../context/SettingsContext";
import { useFeatureFlag } from "../../context/FeatureFlagsContext";
import {
    BulkDeleteWorkersDto,
    BulkUpdateWorkersRequest,
    UpdateWorkerRequest,
    User,
    Worker,
    WorkerQueryFilters,
    WorkerSearchRequest,
    WorkerSearchResponse,
    WorkerTabKey,
    mapWorkerQueryFiltersToSearchRequest,
} from "../../models/worker";
import { useDebounce } from "../../utils/useDebounce";
import { extractErrorMessageSync } from "../../utils/errorHandler";
import ConfirmationModal from "../ConfirmationModel";
import BulkDownloadModal from "./BulkDownloadModal";
import BulkEditModal from "./BulkEditModal";
import UnifiedWorkerFilter, { ClientFilter } from "./UnifiedWorkerFilter";
import WorkerExportModal from "./WorkerExportModal";
import WorkersTableContent from "./WorkersTableContent";
import { WORKER_TABS, WorkerTabCountKey, WorkerTabTone } from "./workerTabs";

type TabKey = WorkerTabKey;

export const WorkersTable: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const location = useLocation();
    const { settings } = useContext(SettingsContext);

    const fetchSequenceRef = useRef(0);

    const [activeTab, setActiveTab] = useState<TabKey>(WorkerTabKey.ALL);
    // Rejected/Blocked is its own view (dedicated toggle); Non-EU is an overlay
    // filter that layers on top of whatever status tab is active.
    const [archiveMode, setArchiveMode] = useState(false);
    const [nonEuMode, setNonEuMode] = useState(false);
    // "Show archived" is a dev-only view (feature-flagged); archived workers are
    // otherwise fully hidden from every list.
    const showArchivedEnabled = useFeatureFlag("showArchivedWorkers");
    const [showArchived, setShowArchived] = useState(false);

    const [workers, setWorkers] = useState<Worker[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce(search, 500);

    const [currentPage, setCurrentPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [clientFilter, setClientFilter] = useState<ClientFilter>({ type: "all" });
    const [additionalFilters, setAdditionalFilters] = useState<WorkerQueryFilters>({});

    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [workerCounts, setWorkerCounts] = useState<{ [key: string]: number } & { unassigned?: number; total?: number }>(
        { unassigned: 0, total: 0 }
    );

    const [bulkSelectMode, setBulkSelectMode] = useState(false);
    const [selectedWorkerIds, setSelectedWorkerIds] = useState<string[]>([]);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [showBulkDownloadModal, setShowBulkDownloadModal] = useState(false);

    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error" | "warning" | "info";
    }>({
        open: false,
        message: "",
        severity: "info",
    });

    const [tabCounts, setTabCounts] = useState<Record<WorkerTabCountKey, number>>({
        [WorkerTabKey.INCOMPLETE]: 0,
        [WorkerTabKey.AWAITING_DOCS]: 0,
        [WorkerTabKey.NEEDS_REVIEW]: 0,
        [WorkerTabKey.CANDIDATES]: 0,
        [WorkerTabKey.PLACED]: 0,
        [WorkerTabKey.RESERVE_POOL]: 0,
        [WorkerTabKey.ALL]: 0,
        nonEu: 0,
        rejectedBlocked: 0,
    });

    useEffect(() => {
        const navState = (location.state as { openTab?: TabKey; importSuccessMessage?: string } | null) ?? null;
        if (!navState) return;

        if (navState.openTab) {
            setActiveTab(navState.openTab);
        }

        if (navState.importSuccessMessage) {
            setSnackbar({
                open: true,
                message: navState.importSuccessMessage,
                severity: "success",
            });
        }

        navigate(location.pathname, { replace: true, state: null });
    }, [location.pathname, location.state, navigate]);

    const currentTab = useMemo(() => WORKER_TABS.find((tab) => tab.key === activeTab) ?? WORKER_TABS.find((tab) => tab.key === WorkerTabKey.ALL) ?? WORKER_TABS[0], [activeTab]);

    const tabColor = useCallback(
        (tone: WorkerTabTone) => {
            switch (tone) {
                case "error":
                    return theme.palette.error.main;
                case "warning":
                    return theme.palette.warning.main;
                case "success":
                    return theme.palette.success.main;
                case "info":
                    return theme.palette.info.main;
                default:
                    return theme.palette.secondary.main;
            }
        },
        [theme.palette]
    );

    const combinedFilters = useMemo(() => {
        const nextFilters: WorkerQueryFilters = {
            ...(currentTab.filters ?? {}),
            ...additionalFilters,
        };

        // Rejected/Blocked is a dedicated view: drop the status-tab filter and
        // ask the backend for ONLY rejected/blocked (properly paginated).
        if (archiveMode) {
            delete (nextFilters as any).status;
            delete (nextFilters as any).statuses;
            (nextFilters as any).rejectedBlockedOnly = true;
        }

        // Non-EU is an overlay: layer it on top of the active status tab.
        if (nonEuMode) {
            (nextFilters as any).nonEuOnly = true;
        }

        if (clientFilter.type === "my-clients" && clientFilter.userId) {
            if (clientFilter.role === "ACCOUNTMANAGER") nextFilters.accountManagerId = clientFilter.userId;
            if (clientFilter.role === "RECRUITER") nextFilters.recruiterId = clientFilter.userId;
        }

        if (clientFilter.type === "specific-user" && clientFilter.userId) {
            if (clientFilter.role === "ACCOUNTMANAGER") nextFilters.accountManagerId = clientFilter.userId;
            if (clientFilter.role === "RECRUITER") nextFilters.recruiterId = clientFilter.userId;
        }

        if (clientFilter.type === "unassigned") {
            nextFilters.accountManagerId = null;
            nextFilters.recruiterId = null;
        }

        // Show-archived is a dedicated view: return ONLY archived workers.
        // When off, no `archived` filter is sent and the backend excludes them.
        if (showArchived) nextFilters.archived = true;

        return nextFilters;
    }, [additionalFilters, archiveMode, nonEuMode, clientFilter, currentTab.filters, showArchived]);

    const activeFilterEntries = useMemo(() => {
        return Object.entries(additionalFilters).filter(([, value]) => {
            if (value === undefined || value === null) return false;
            if (typeof value === "string") return value.trim().length > 0;
            if (Array.isArray(value)) return value.length > 0;
            if (typeof value === "boolean") return value;
            return true;
        });
    }, [additionalFilters]);
    const activeFilterCount = activeFilterEntries.length;

    const fetchWorkers = useCallback(
        async (isRefresh = false) => {
            const fetchSeq = ++fetchSequenceRef.current;
            if (!settings) return;

            if (isRefresh) setRefreshing(true);
            else setLoading(true);

            try {
                const mappedFilters = mapWorkerQueryFiltersToSearchRequest(combinedFilters);
                const requestBody: WorkerSearchRequest = {
                    page: currentPage + 1,
                    limit: rowsPerPage,
                    searchParam: debouncedSearch,
                    ...mappedFilters,
                };

                const response: WorkerSearchResponse = await fetchWorkersRequest(requestBody);
                if (fetchSequenceRef.current !== fetchSeq) return;

                // Rejected/Blocked filtering now happens server-side via
                // rejectedBlockedOnly, so pagination + counts are correct.
                setWorkers(response.workers);
                setTotalCount(response.totalCount || response.workers.length);
            } catch (error) {
                const errorMessage = extractErrorMessageSync(error);
                setSnackbar({ open: true, message: errorMessage, severity: "error" });
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [
            archiveMode,
            combinedFilters,
            currentPage,
            debouncedSearch,
            rowsPerPage,
            settings,
        ]
    );

    const fetchTabCounts = useCallback(async () => {
        if (!settings) return;

        try {
            const baseFilter: Partial<WorkerSearchRequest> = {};

            if (clientFilter.type === "my-clients" && clientFilter.userId) {
                if (clientFilter.role === "ACCOUNTMANAGER") baseFilter.accountManagerId = clientFilter.userId;
                if (clientFilter.role === "RECRUITER") baseFilter.recruiterId = clientFilter.userId;
            } else if (clientFilter.type === "specific-user" && clientFilter.userId) {
                if (clientFilter.role === "ACCOUNTMANAGER") baseFilter.accountManagerId = clientFilter.userId;
                if (clientFilter.role === "RECRUITER") baseFilter.recruiterId = clientFilter.userId;
            } else if (clientFilter.type === "unassigned") {
                baseFilter.accountManagerId = null;
                baseFilter.recruiterId = null;
            }

            const mappedAdditionalFilters = mapWorkerQueryFiltersToSearchRequest(additionalFilters);
            Object.assign(baseFilter, mappedAdditionalFilters);

            // Single request replaces the old 8-request per-status fan-out —
            // the backend groups by status once and returns every tab/badge count.
            const counts = await fetchWorkerCountsByStatus(baseFilter);
            setTabCounts({
                [WorkerTabKey.INCOMPLETE]: counts.incomplete,
                [WorkerTabKey.AWAITING_DOCS]: counts.pendingDocuments,
                [WorkerTabKey.NEEDS_REVIEW]: counts.pendingApproval,
                [WorkerTabKey.CANDIDATES]: counts.candidate,
                [WorkerTabKey.PLACED]: counts.placed,
                [WorkerTabKey.RESERVE_POOL]: counts.reservePool,
                [WorkerTabKey.ALL]: counts.all,
                nonEu: counts.nonEu,
                rejectedBlocked: counts.rejectedBlocked,
            });
        } catch {
            // Non-blocking; keep previous counts.
        }
    }, [additionalFilters, clientFilter, settings]);

    useEffect(() => {
        if (settings) {
            fetchWorkers();
        }
    }, [activeTab, fetchWorkers, settings]);

    useEffect(() => {
        if (settings) fetchTabCounts();
    }, [fetchTabCounts, settings]);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                await usersApi.syncUsersFromKeycloak();
                const users = await usersApi.getRecruitmentUsers();
                setAllUsers(users);
            } catch {
                // Non-blocking
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchCounts = async () => {
            if (!settings || allUsers.length === 0) return;

            try {
                const mappedAdditionalFilters = mapWorkerQueryFiltersToSearchRequest(additionalFilters);
                const { byUser, unassigned, total } = await fetchWorkerCountsByAssignee(mappedAdditionalFilters);

                const counts: { [key: string]: number } & { unassigned?: number; total?: number } = { ...byUser };
                counts.unassigned = unassigned;
                counts.total = total;
                setWorkerCounts(counts);
            } catch {
                // Non-blocking
            }
        };

        fetchCounts();
    }, [additionalFilters, allUsers, settings]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        const nextTab = newValue as TabKey;
        setActiveTab(nextTab);
        setArchiveMode(false);
        setCurrentPage(0);
        setSelectedWorkerIds([]);
        setBulkSelectMode(false);
    };

    const handleUnifiedFilterChange = (nextClientFilter: ClientFilter, advancedFilters: WorkerQueryFilters) => {
        setClientFilter(nextClientFilter);
        setAdditionalFilters(advancedFilters);
        setCurrentPage(0);

        if (advancedFilters.statuses && advancedFilters.statuses.length > 0) {
            const tabStatus = WORKER_TABS.find((tab) => tab.key === activeTab)?.filters?.status;
            const currentTabStatuses = tabStatus ? [tabStatus] : [];
            const hasNonTabStatus = advancedFilters.statuses.some((status) => !currentTabStatuses.includes(status));
            if (hasNonTabStatus && activeTab !== WorkerTabKey.ALL) setActiveTab(WorkerTabKey.ALL);
        }
    };

    const handleToggleBulkSelectMode = () => {
        setBulkSelectMode((prev) => {
            if (prev) {
                setSelectedWorkerIds([]);
            }
            return !prev;
        });
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) setSelectedWorkerIds(workers.map((w) => w.workerId));
        else setSelectedWorkerIds([]);
    };

    const handleSelectRow = (workerId: string, checked: boolean) => {
        if (checked) setSelectedWorkerIds((prev) => [...prev, workerId]);
        else setSelectedWorkerIds((prev) => prev.filter((id) => id !== workerId));
    };

    const handleConfirmBulkDelete = async () => {
        try {
            const requestBody: BulkDeleteWorkersDto = { workerIds: selectedWorkerIds };
            await bulkDeleteWorkersRequest(requestBody);

            setShowDeleteModal(false);
            setSelectedWorkerIds([]);
            setBulkSelectMode(false);
            setSnackbar({
                open: true,
                message: `${selectedWorkerIds.length} worker(s) deleted successfully!`,
                severity: "success",
            });
            fetchWorkers(true);
            fetchTabCounts();
        } catch (error) {
            const errorMessage = extractErrorMessageSync(error);
            setSnackbar({ open: true, message: errorMessage, severity: "error" });
        }
    };

    const handleConfirmBulkEdit = async (formData: UpdateWorkerRequest) => {
        try {
            const requestBody: BulkUpdateWorkersRequest = { workerIds: selectedWorkerIds, updates: formData };
            await bulkUpdateWorkersRequest(requestBody);

            setShowBulkEditModal(false);
            setSelectedWorkerIds([]);
            setBulkSelectMode(false);
            setSnackbar({
                open: true,
                message: `${selectedWorkerIds.length} worker(s) updated successfully!`,
                severity: "success",
            });
            fetchWorkers(true);
            fetchTabCounts();
        } catch (error) {
            const errorMessage = extractErrorMessageSync(error);
            setSnackbar({ open: true, message: errorMessage, severity: "error" });
        }
    };

    const handleExport = () => setShowExportModal(true);

    const handleExportAll = async (): Promise<Worker[]> => {
        try {
            const mappedFilters = mapWorkerQueryFiltersToSearchRequest(combinedFilters);
            const requestBody: WorkerSearchRequest = {
                page: 1,
                limit: 10000,
                searchParam: debouncedSearch,
                ...mappedFilters,
            };
            const response: WorkerSearchResponse = await fetchWorkersRequest(requestBody);
            return response.workers;
        } catch {
            return [];
        }
    };

    const createWorker = () => {
        const newWorkerId = uuidv4();
        navigate(`/workers/${newWorkerId}?mode=create`);
    };

    const toggleArchiveMode = () => {
        setArchiveMode((prev) => {
            const next = !prev;
            if (next) {
                setActiveTab(WorkerTabKey.ALL);
                setCurrentPage(0);
                setSelectedWorkerIds([]);
                setBulkSelectMode(false);
            }
            return next;
        });
    };

    // Non-EU is an overlay filter, not a dedicated view — it layers on top of
    // the active status tab, so we don't reset the tab (just re-page).
    const toggleNonEuMode = () => {
        setNonEuMode((prev) => !prev);
        setCurrentPage(0);
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2, minHeight: 0 }}>
            <Paper
                variant="outlined"
                sx={{
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 3,
                    p: { xs: 2, md: 2.5 },
                    borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background:
                            theme.palette.mode === "dark"
                                ? `radial-gradient(800px circle at 20% -10%, ${alpha(theme.palette.primary.main, 0.25)} 0%, transparent 55%),
                                   radial-gradient(700px circle at 90% 0%, ${alpha(theme.palette.secondary.main, 0.18)} 0%, transparent 55%)`
                                : `radial-gradient(800px circle at 20% -10%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 55%),
                                   radial-gradient(700px circle at 90% 0%, ${alpha(theme.palette.secondary.main, 0.14)} 0%, transparent 55%)`,
                        pointerEvents: "none",
                    }}
                />

                <Stack
                    direction={{ xs: "column", md: "row" }}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ position: "relative" }}
                >
                    <Box>
                        <Typography variant="h4" fontWeight={800} letterSpacing={-0.3}>
                            Workers
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Search, filter, and manage worker profiles.
                        </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title={nonEuMode ? "Showing Non-EU only — click to clear" : "Non-EU only (layers on the current tab)"}>
                            <IconButton
                                onClick={toggleNonEuMode}
                                size={isMobile ? "small" : "medium"}
                                color={nonEuMode ? "primary" : "default"}
                                sx={{ opacity: nonEuMode ? 1 : 0.75 }}
                            >
                                <PublicIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title={archiveMode ? "Exit Rejected/Blocked view" : "Rejected / Blocked"}>
                            <IconButton
                                onClick={toggleArchiveMode}
                                size={isMobile ? "small" : "medium"}
                                color={archiveMode ? "primary" : "default"}
                                sx={{ opacity: archiveMode ? 1 : 0.75 }}
                            >
                                <DeleteOutlineIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                        </Tooltip>

                        {showArchivedEnabled && (
                            <Tooltip title={showArchived ? "Showing archived — click to exit" : "Show archived workers (dev)"}>
                                <IconButton
                                    onClick={() => { setShowArchived((p) => !p); setCurrentPage(0); }}
                                    size={isMobile ? "small" : "medium"}
                                    color={showArchived ? "primary" : "default"}
                                    sx={{ opacity: showArchived ? 1 : 0.75 }}
                                >
                                    <ArchiveIcon fontSize={isMobile ? "small" : "medium"} />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title="Refresh">
                            <IconButton
                                onClick={() => fetchWorkers(true)}
                                disabled={refreshing}
                                size={isMobile ? "small" : "medium"}
                                color="primary"
                            >
                                <RefreshIcon fontSize={isMobile ? "small" : "medium"} />
                            </IconButton>
                        </Tooltip>

                        <Button
                            variant="contained"
                            startIcon={<PersonAddIcon />}
                            onClick={createWorker}
                            size={isMobile ? "small" : "medium"}
                            sx={{ borderRadius: 999 }}
                        >
                            New worker
                        </Button>
                    </Stack>
                </Stack>

                <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mt: 2, position: "relative" }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Search by name, email, phone, or worker ID…"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(0);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                            endAdornment: search ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined,
                        }}
                        size={isMobile ? "small" : "medium"}
                    />

                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" justifyContent="flex-end">
                        {activeFilterCount > 0 && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`${activeFilterCount} filter${activeFilterCount === 1 ? "" : "s"}`}
                                sx={{ backgroundColor: alpha(theme.palette.background.paper, 0.6) }}
                            />
                        )}
                        <Chip
                            size="small"
                            label={`${totalCount.toLocaleString()} result${totalCount === 1 ? "" : "s"}`}
                            sx={{
                                backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === "dark" ? 0.2 : 0.08),
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                            }}
                        />
                    </Stack>
                </Stack>
            </Paper>

            <UnifiedWorkerFilter
                users={allUsers}
                onFilterChange={handleUnifiedFilterChange}
                workerCounts={workerCounts}
                initialAdvancedFilters={additionalFilters}
                onToggleBulkSelect={handleToggleBulkSelectMode}
                onExport={handleExport}
                bulkSelectMode={bulkSelectMode}
                selectedCount={selectedWorkerIds.length}
            />

            {(activeFilterCount > 0 || archiveMode || nonEuMode) && (
                <Paper
                    variant="outlined"
                    sx={{
                        p: 1,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                    }}
                >
                    {archiveMode && (
                        <Chip
                            icon={<DeleteOutlineIcon fontSize="small" />}
                            label="Rejected / Blocked"
                            onDelete={toggleArchiveMode}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    {nonEuMode && (
                        <Chip
                            icon={<PublicIcon fontSize="small" />}
                            label="Non-EU only"
                            onDelete={toggleNonEuMode}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                    {activeFilterEntries.map(([key, value]) => {
                        let displayValue: string = "";
                        if (key === "statuses" && Array.isArray(value)) {
                            displayValue = value
                                .map((s) =>
                                    s
                                        .replace(/_/g, " ")
                                        .toLowerCase()
                                        .replace(/\b\w/g, (l: string) => l.toUpperCase())
                                )
                                .join(", ");
                        } else if (Array.isArray(value)) {
                            displayValue = value.join(", ");
                        } else if (typeof value === "boolean") {
                            displayValue = value ? "Yes" : "No";
                        } else {
                            displayValue = String(value);
                        }

                        return (
                            <Chip
                                key={key}
                                label={`${key.replace(/([A-Z])/g, " $1").trim()}: ${displayValue}`}
                                onDelete={() => {
                                    setAdditionalFilters((prev) => {
                                        const next = { ...prev } as any;
                                        delete next[key];
                                        return next;
                                    });
                                }}
                                size="small"
                                variant="outlined"
                            />
                        );
                    })}
                </Paper>
            )}

            {bulkSelectMode && selectedWorkerIds.length > 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.5 : 0.35)}`,
                        backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.2 : 0.08),
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                    }}
                >
                    <Typography variant="body2" fontWeight={700}>
                        {selectedWorkerIds.length} selected
                    </Typography>
                    <Box sx={{ flex: 1 }} />
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Button variant="contained" size="small" color="info" onClick={() => setShowBulkEditModal(true)}>
                            Edit
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            onClick={() => setShowBulkDownloadModal(true)}
                        >
                            Download CVs
                        </Button>
                        <Button variant="contained" size="small" color="error" onClick={() => setShowDeleteModal(true)}>
                            Delete
                        </Button>
                    </Stack>
                </Paper>
            )}

            {!archiveMode && (
                <Paper
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                    }}
                >
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        variant={isMobile ? "scrollable" : "fullWidth"}
                        scrollButtons={isMobile ? "auto" : false}
                        sx={{
                            "& .MuiTabs-indicator": {
                                height: 3,
                                backgroundColor: tabColor(currentTab.tone),
                            },
                            "& .MuiTab-root": {
                                textTransform: "none",
                                minHeight: 64,
                                py: 1.25,
                            },
                        }}
                    >
                        {WORKER_TABS.map((tab) => {
                            const color = tabColor(tab.tone);
                            const count = tabCounts[tab.key];
                            const isSelected = activeTab === tab.key;

                            return (
                                <Tab
                                    key={tab.key}
                                    value={tab.key}
                                    sx={{
                                        "&.Mui-selected": { color: theme.palette.text.primary },
                                        backgroundColor: isSelected
                                            ? alpha(color, theme.palette.mode === "dark" ? 0.25 : 0.12)
                                            : "transparent",
                                        borderRight: { xs: "none", md: `1px solid ${alpha(theme.palette.divider, 0.6)}` },
                                    }}
                                    label={
                                        <Stack spacing={0.25} alignItems="center">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Typography
                                                    fontWeight={800}
                                                    sx={{ color: isSelected ? color : theme.palette.text.primary }}
                                                >
                                                    {tab.label}
                                                </Typography>
                                                {typeof count === "number" && (
                                                    <Chip
                                                        size="small"
                                                        label={count.toLocaleString()}
                                                        sx={{
                                                            height: 22,
                                                            fontWeight: 800,
                                                            backgroundColor: alpha(
                                                                color,
                                                                theme.palette.mode === "dark" ? 0.35 : 0.18
                                                            ),
                                                            color: theme.palette.text.primary,
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: { xs: "none", sm: "block" } }}
                                            >
                                                {tab.description}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            );
                        })}
                    </Tabs>
                </Paper>
            )}

            <Paper
                variant="outlined"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "hidden",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
                }}
            >
                {refreshing && <LinearProgress />}

                {loading && !refreshing ? (
                    <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : workers.length === 0 ? (
                    <Box sx={{ flex: 1, display: "grid", placeItems: "center", p: 3, textAlign: "center" }}>
                        <Stack spacing={0.5} alignItems="center">
                            <Typography variant="h6" fontWeight={800}>
                                No workers found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {search || activeFilterCount > 0
                                    ? "Try adjusting your search or filters."
                                    : "Create a worker to get started."}
                            </Typography>
                            {!search && activeFilterCount === 0 && (
                                <Button variant="contained" sx={{ mt: 1, borderRadius: 999 }} onClick={createWorker}>
                                    New worker
                                </Button>
                            )}
                        </Stack>
                    </Box>
                ) : (
                    <WorkersTableContent
                        workers={workers}
                        bulkSelectMode={bulkSelectMode}
                        selectedWorkerIds={selectedWorkerIds}
                        onSelectAll={handleSelectAll}
                        onSelectRow={handleSelectRow}
                        currentPage={currentPage}
                        rowsPerPage={rowsPerPage}
                        totalCount={totalCount}
                        onPageChange={setCurrentPage}
                        onRowsPerPageChange={setRowsPerPage}
                    />
                )}
            </Paper>

            <ConfirmationModal
                open={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleConfirmBulkDelete}
                message={`Are you sure you want to delete ${selectedWorkerIds.length} selected worker(s)?`}
            />

            <BulkEditModal open={showBulkEditModal} onClose={() => setShowBulkEditModal(false)} onSave={handleConfirmBulkEdit} />

            <WorkerExportModal
                open={showExportModal}
                onClose={() => setShowExportModal(false)}
                workers={workers}
                totalCount={totalCount}
                onExportAll={handleExportAll}
            />

            <BulkDownloadModal
                open={showBulkDownloadModal}
                workerIds={selectedWorkerIds}
                onClose={() => setShowBulkDownloadModal(false)}
                onSuccess={() => {
                    setShowBulkDownloadModal(false);
                    setSelectedWorkerIds([]);
                    setBulkSelectMode(false);
                    setSnackbar({
                        open: true,
                        message: `Successfully downloaded ${selectedWorkerIds.length} CV${
                            selectedWorkerIds.length !== 1 ? "s" : ""
                        } as ZIP file`,
                        severity: "success",
                    });
                }}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};
